// Data access layer for outbreak rows. The whole frontend reads outbreaks through here.
import { supabase, supabaseConfigured } from './supabase';
import type { OutbreakWithSources } from '../types/supabase';

export async function fetchOutbreaks(): Promise<OutbreakWithSources[]> {
  if (!supabaseConfigured) return [];
  const { data, error } = await supabase
    .from('outbreaks_with_sources')
    .select('*')
    .order('start_date', { ascending: false });
  if (error) {
    console.error('[outbreaks] fetch failed', error);
    throw error;
  }
  return (data ?? []) as OutbreakWithSources[];
}

export function totalCases(o: OutbreakWithSources): number {
  return o.cases_confirmed + o.cases_suspected;
}

export function isActive(o: OutbreakWithSources): boolean {
  return o.status === 'active';
}

// Geographic + temporal filters applied client-side. The dataset is small
// (<1k rows for the foreseeable future), so streaming + filtering in-browser
// keeps the UX snappy.
//
// Filter semantics — an event is in-window if EITHER its start_date OR its
// most recent case-count update (cases_last_updated) falls inside [from, to].
// A "last 7 days" preset therefore surfaces both newly-reported outbreaks
// AND long-running outbreaks that received a fresh case count this week
// (which is what users actually mean). Previously this was strict-by-start,
// which hid the MV Hondius cluster from every "last 7 days" lookup despite
// it accumulating new confirmed cases daily — that read as "the map is
// broken." Stats stay honest because totalCases() / aggregateStats() count
// the full historical event total once it's visible, not just deltas in the
// window — that's a separate UX problem to solve if it comes up.
import { dayIndexFromDate } from './url-state';

export interface FilterState {
  dayRange: [number, number];
  pathogens: Set<string>;
  syndromes: Set<string>;
  statuses: Set<string>;
  confidences: Set<string>;
}

export function applyFilters(rows: OutbreakWithSources[], f: FilterState): OutbreakWithSources[] {
  const [from, to] = f.dayRange;
  return rows.filter(o => {
    const startIdx = dayIndexFromDate(o.start_date);
    const updatedIdx = o.cases_last_updated
      ? dayIndexFromDate(o.cases_last_updated)
      : startIdx;
    const inWindow =
      (startIdx >= from && startIdx <= to) ||
      (updatedIdx >= from && updatedIdx <= to);
    if (!inWindow) return false;
    if (f.pathogens.size && !f.pathogens.has(o.pathogen)) return false;
    if (f.syndromes.size && !f.syndromes.has(o.syndrome)) return false;
    if (f.statuses.size && !f.statuses.has(o.status)) return false;
    if (f.confidences.size && !f.confidences.has(o.confidence)) return false;
    return true;
  });
}

/** All-time aggregate stats for a slice of outbreaks. Used in the header strip. */
export function aggregateStats(rows: OutbreakWithSources[]): {
  events: number;
  cases: number;
  confirmed: number;
  suspected: number;
  deaths: number;
  countries: number;
} {
  let confirmed = 0;
  let suspected = 0;
  let deaths = 0;
  const countries = new Set<string>();
  for (const o of rows) {
    confirmed += o.cases_confirmed;
    suspected += o.cases_suspected;
    deaths += o.cases_deaths;
    for (const c of o.countries_affected) countries.add(c);
  }
  return {
    events: rows.length,
    cases: confirmed + suspected,
    confirmed,
    suspected,
    deaths,
    countries: countries.size,
  };
}
